import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  count,
  create,
  createAssociation,
  Employer,
  fetch,
  get,
  InterimAgency,
  Organization,
  OrganizationCreateAssociationPayload,
  OrganizationCreationPayload,
  OrganizationUpdateAssociationPayload,
  OrganizationUpdatePayload,
  update,
  updateAssociation,
  discontinueAssociation,
  reactivateAssociation,
  OrganizationAssociationActionPayload,
} from '../api/organizations'
import useStore from '../store'
import { userKeys } from './users'
import { compareDocsById } from '../utils/documents'

export const organizationKeys = {
  all: ['organizations'] as const,
  mutations: {
    create:                   ['organizations', 'create']                    as const,
    update:                   ['organizations', 'update']                    as const,
    associationCreate:        ['organizations', 'associations', 'create']    as const,
    associationUpdate:        ['organizations', 'associations', 'update']    as const,
    associationDiscontinue:   ['organizations', 'associations', 'discontinue'] as const,
    associationReactivate:    ['organizations', 'associations', 'reactivate']  as const,
  },
} as const

//Queries
export const useOrganizationQuery = <T = Organization>(
  id?: string,
  type?: 'interimAgency' | 'employer',
  options?: QueryOptions<T>,
): UseQueryResult<T, any> =>
  useResource<T, { type?: 'interimAgency' | 'employer' }>(
    'organizations',
    get as (params: { id: string } & { type?: 'interimAgency' | 'employer' }) => Promise<T>,
    id,
    {
      type,
    },
    {
      ...options,
      onSuccess(data) {
        const { session } = useStore.getState()
        // For Super Admin logged as organization
        // we update the currently stored organization if the fecthed is the stored one
        if (
          session.isSuperAdmin &&
          session.loggedAs?.organization?._id &&
          (data as Organization)._id === session.loggedAs.organization._id
        ) {
          session.initializeSession(
            session.currentUser,
            session.jwt,
            session.currentUserRole,
            data as Organization,
            session.currentOrganizationRole,
          )
        }
      },
    },
  )

export const useOrganizationsQuery = <T = Organization>(
  filters?: any,
  options?: QueryOptions<T[]>,
): UseQueryResult<T[], any> => useResources<T[], any>('organizations', fetch as (params: any) => Promise<T[]>, filters, options)

/**
 * Hook to get the count of organizations matching the given filters
 * @param filters - Optional filters to apply when counting organizations
 * @param options - Optional React Query options
 * @returns UseQueryResult containing the count of matching organizations
 */
export const useOrganizationsCountQuery = (
  filters?: any,
  options?: QueryOptions<number>,
): UseQueryResult<number, any> =>
  useResources<number, any>('organizations/count', count, filters, options)

export const useAssociatedOrganizationsQuery = <T = Organization>(
  options?: QueryOptions<T[]>,
): UseQueryResult<T[], any> => {
  const currentOrganization = useStore.getState().session.currentOrganization as Organization
  const currentOrganizationType = currentOrganization.type

  return useResources<T[], any>(
    'organizations',
    fetch as (params: any) => Promise<T[]>,
    { type: currentOrganizationType === 'employer' ? 'interimAgency' : 'employer' },
    {
      ...(options || {}),
      select(res) {
        return res.filter((organization: T) => {
          const employer = (
            currentOrganizationType === 'employer' ? currentOrganization : organization
          ) as Employer
          const interimAgency = (
            currentOrganizationType === 'interimAgency' ? currentOrganization : organization
          ) as InterimAgency
          return employer.associations.some(
            a => a.status === 'accepted' && compareDocsById(a.interimAgency, interimAgency),
          )
        })
      },
    },
  )
}

//Mutations
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['users'])
  queryClient.invalidateQueries(['organizations'])
  queryClient.invalidateQueries(userKeys.isAuth)
}

export const useCreateMutation = (): UseMutationResult<Organization, OrganizationCreationPayload> =>
  useMutation(create, { mutationKey: organizationKeys.mutations.create })

export const useUpdateMutation = (): UseMutationResult<Organization, OrganizationUpdatePayload> =>
  useMutation(update, {
    mutationKey: organizationKeys.mutations.update,
    onSuccess: onMutationSuccess,
  })

export const useCreateAssociationMutation = (): UseMutationResult<
  Organization,
  OrganizationCreateAssociationPayload
> =>
  useMutation(createAssociation, {
    mutationKey: organizationKeys.mutations.associationCreate,
    onSuccess: onMutationSuccess,
  })

export const useUpdateAssocationMutation = (): UseMutationResult<
  Organization,
  OrganizationUpdateAssociationPayload
> =>
  useMutation(updateAssociation, {
    mutationKey: organizationKeys.mutations.associationUpdate,
    onSuccess: onMutationSuccess,
  })

export const useDiscontinueAssociationMutation = (): UseMutationResult<
  Organization,
  OrganizationAssociationActionPayload
> =>
  useMutation(discontinueAssociation, {
    mutationKey: organizationKeys.mutations.associationDiscontinue,
    onSuccess: onMutationSuccess,
  })

export const useReactivateAssociationMutation = (): UseMutationResult<
  Organization,
  OrganizationAssociationActionPayload
> =>
  useMutation(reactivateAssociation, {
    mutationKey: organizationKeys.mutations.associationReactivate,
    onSuccess: onMutationSuccess,
  })
